import React, { createRef } from "react";
import DOMPurify from "dompurify";
import { UserTypes } from "shared-types";
import { Accordion, AccordionAction } from "components/common/Accordion";
import { AgencyGrid } from "../AgencyGrid";

import { sortArray } from "shared/utils/array";
import { UserRoleManager, UserRoles } from "core";
import { IAgency, IAgencyList } from "models";
import { IAgenciesPageContent } from "../../AgenciesPageContent";

import { RadioGroupFormik } from "component-library";
import { Formik } from "formik";
import { agenciesPageFallback } from "pages/AgenciesPage/fallbackTranslations";
import "./AgenciesForm.scss";

export const SPOTLIGHT_DEFAULT_AGENCY_REF = 50;

// Bring back helper when copy is written for SL-1518
// const lookingForRepresentationHelperProps = {
//   tooltip: {
//     texts: {
//       header: agenciesPageFallback.seekingAgencyHelperHeader,
//       content: agenciesPageFallback.seekingAgencyHelperDescription,
//       closeButton: agenciesPageFallback.seekingAgencyHelperCloseButtonAriaLabel,
//       tooltipName: "looking-for-representation-helper",
//     },
//     config: {
//       minimalTopDistance: 50,
//     },
//   },
// };

interface FormFields {
  isLookingForRepresentation: string;
}

export interface IAgenciesFormProps {
  agencyList?: IAgencyList;
  content: IAgenciesPageContent;
  deleteAgency: (id: number) => void;
  updateList: (agencyList: IAgencyList) => void;
  handleHelpRedirection: EventListener;
  toggleLookingForRepresentation: (form: any) => void;
}

export class AgenciesForm extends React.Component<IAgenciesFormProps> {
  private infoRef = createRef<HTMLDivElement>();

  public render() {
    const { agencyList, content } = this.props;

    return (
      <div className="c-contacts-form">
        <div className="c-contacts-form__description">
          {content.description}
        </div>
        {agencyList && agencyList.agents.length > 0
          ? this.renderAccordion(agencyList)
          : this.renderAltContent()}
        {this.renderInfo()}
        {this.renderSelfRepresentedForm()}
      </div>
    );
  }

  public componentDidUpdate(prevProps: IAgenciesFormProps) {
    const { agencyList, content } = this.props;
    const { agencyList: prevAgencyList, content: prevContent } = prevProps;

    if (prevAgencyList !== agencyList || prevContent !== content) {
      this.toggleAnchorsListeners(true);
    }
  }

  public componentWillUnmount() {
    this.toggleAnchorsListeners();
  }

  private renderAccordion = (agencyList: IAgencyList) => {
    const agencyDataSet = this.mapAccordionDataSet(agencyList.agents);

    return (
      <Accordion
        actions={this.getActiveActions()}
        actionHandler={this.accordionEventHandler}
        accordionRowsData={agencyDataSet}
        isInitiallyExpanded={true}
        itemsReordered={this.updateAgencyListSequence}
      />
    );
  };

  private renderAltContent = () => {
    const { content } = this.props;

    return (
      <div className="c-contacts-form__alt-content">{content.noAgents}</div>
    );
  };

  private renderInfo = () => {
    const { content, agencyList } = this.props;
    const agencies = (agencyList && agencyList.agents) || [];

    let informationContent = "";

    const isOnlyDefaultSpotlightAgency =
      agencies.length === 1 &&
      agencies[0].refNumber === SPOTLIGHT_DEFAULT_AGENCY_REF;
    const isOnlyYoungPerformerAgency =
      this.isYoungPerformer() && agencies.length === 1;

    if (isOnlyDefaultSpotlightAgency) {
      informationContent = content.commonInformation;
    }

    if (isOnlyYoungPerformerAgency) {
      informationContent = content.youngPerformerInformation;
    }

    if (agencies.length > 1) {
      informationContent = content.agencyListFullInformation;
    }

    return (
      Boolean(informationContent) && (
        <div
          className="c-contacts-form__info"
          ref={isOnlyYoungPerformerAgency ? this.infoRef : null}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(informationContent),
          }}
        />
      )
    );
  };

  private renderSelfRepresentedForm = () => {
    const { agencyList } = this.props;
    const isLookingForRepresentation =
      agencyList?.isLookingForRepresentation === undefined
        ? "false"
        : agencyList?.isLookingForRepresentation.toString();
    const isSelfRepresented = agencyList?.isSelfRepresented;
    const isPerformer = UserRoleManager.isInRole(UserRoles.performer);

    const handleLookingForRepresentationChange = (value) => {
      const isSeeking = value === "true";
      this.props.toggleLookingForRepresentation(isSeeking);
    };

    // const lookingForRepresentationTooltip = () => (
    //   <QuestionTooltip {...lookingForRepresentationHelperProps} />
    // );

    if (
      !isSelfRepresented ||
      !isPerformer ||
      isLookingForRepresentation === undefined
    ) {
      return null;
    }

    return (
      <div className="mt-3 flex flex-col gap-4 text-sm md:text-l lg:max-w-1/2">
        <div>{agenciesPageFallback.selfRepresented}</div>
        <Formik<FormFields>
          initialValues={{ isLookingForRepresentation }}
          onSubmit={() => {}} // handled by closing the modal
        >
          {(props) => {
            const seekingSelected = JSON.parse(
              props.values.isLookingForRepresentation
            );

            return (
              <div>
                <RadioGroupFormik
                  name="isLookingForRepresentation"
                  label={agenciesPageFallback.seekingAgencyLabel}
                  // tooltipComponent={lookingForRepresentationTooltip}
                  tooltipPosition="start"
                  radioButtons={[
                    {
                      label: agenciesPageFallback.seekingAgencyYes,
                      value: "true",
                      buttonClassName: seekingSelected ? "font-semibold" : "",
                    },
                    {
                      label: agenciesPageFallback.seekingAgencyNo,
                      value: "false",
                      buttonClassName: seekingSelected ? "" : "font-semibold",
                    },
                  ]}
                  onChange={handleLookingForRepresentationChange}
                />
                <div className="flex gap-4 bg-color-neutral-three p-2.5">
                  {seekingSelected
                    ? agenciesPageFallback.seekingAgencyYesDescription
                    : agenciesPageFallback.seekingAgencyNoDescription}
                </div>
              </div>
            );
          }}
        </Formik>
      </div>
    );
  };

  private toggleAnchorsListeners = (addListener: boolean = false) => {
    if (this.infoRef && this.infoRef.current) {
      const links = this.infoRef.current.querySelectorAll("a");
      const action = addListener ? "addEventListener" : "removeEventListener";
      const helpLink = Array.from(links).find((link) => link.hash === "#help");

      if (helpLink) {
        helpLink[action]("click", this.props.handleHelpRedirection, false);
      }
    }
  };

  private mapAccordionDataSet = (agencies: IAgency[]) =>
    agencies.map((agency) => ({
      uniqueKey: agency.refNumber,
      header: agency.name,
      renderChildren: (data: IAgency) => <AgencyGrid model={data} />,
      data: agency,
    }));

  private accordionEventHandler = (
    uniqueKey: number,
    eventType: AccordionAction
  ) => {
    if (eventType === AccordionAction.delete) {
      this.props.deleteAgency(uniqueKey);
    }
  };

  private updateAgencyListSequence = (newKeySequence: number[]) => {
    const newSequence = this.orderListByKeys(newKeySequence);

    if (newSequence) {
      this.props.updateList(newSequence);
    }
  };

  private orderListByKeys = (newSequence: number[]) => {
    const { agencyList } = this.props;

    if (agencyList) {
      const reorderedAgencyList = sortArray(agencyList.agents, [
        {
          expression: (agency) => newSequence.indexOf(agency.refNumber),
          order: "asc",
        },
      ]);

      agencyList.agents = reorderedAgencyList;
    }

    return agencyList;
  };

  private getActiveActions = () => {
    const { agencyList } = this.props;
    const availableActions: AccordionAction[] = [];

    if (agencyList) {
      const { agents } = agencyList;

      if (agents.length > 1) {
        availableActions.push(
          AccordionAction.delete,
          AccordionAction.arrowUp,
          AccordionAction.arrowDown
        );
      } else if (
        agents.length === 1 &&
        agents[0].refNumber !== SPOTLIGHT_DEFAULT_AGENCY_REF &&
        !this.isYoungPerformer()
      ) {
        availableActions.push(AccordionAction.delete);
      }
    }

    return availableActions;
  };

  private isYoungPerformer = () =>
    UserRoleManager.isUserType(UserTypes.youngPerformerFemale) ||
    UserRoleManager.isUserType(UserTypes.youngPerformerMale);
}

export default AgenciesForm;
